/**
 * Sprint board admin gate: uses Supabase Auth. Only users with app_metadata.sprint_board_admin = true can access.
 * In Dashboard: Authentication → Users → select user → Edit user → Application Metadata: { "sprint_board_admin": true }
 * Session stored in sessionStorage; we sign in to verify, then sign out so the main app session is untouched.
 */

import { createClient } from '@supabase/supabase-js';

const SESSION_KEY = 'sprint_board_admin';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

/** Separate client so we don't overwrite the main app's auth session. Used for admin verify and for all board data (RLS requires authenticated). */
export function getSprintBoardAuthClient() {
  if (!supabaseUrl || !supabaseAnonKey) throw new Error('REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY are required');
  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
      storageKey: 'sprint_board_auth_verify',
    },
  });
}

export async function verifySprintBoardAdmin(email: string, password: string): Promise<{ ok: boolean; message?: string }> {
  const client = getSprintBoardAuthClient();
  const { data, error } = await client.auth.signInWithPassword({
    email: email.trim(),
    password,
  });
  if (error) {
    await client.auth.signOut();
    return { ok: false, message: 'Invalid email or password' };
  }
  const isAdmin = data.user?.app_metadata?.sprint_board_admin === true;
  if (!isAdmin) {
    await client.auth.signOut();
    return { ok: false, message: 'Not authorized to access the sprint board.' };
  }
  /* Keep session so board fetch/mutations run as authenticated (RLS). */
  return { ok: true };
}

export function setSprintBoardAdminSession(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(SESSION_KEY, '1');
  }
}

export function clearSprintBoardAdminSession(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(SESSION_KEY);
  }
}

/** Call when user clicks "Sign out (admin)" – signs out from board client and clears session flag. */
export async function signOutSprintBoardAdmin(): Promise<void> {
  clearSprintBoardAdminSession();
  try {
    await getSprintBoardAuthClient().auth.signOut();
  } catch {
    // ignore
  }
}

export function hasSprintBoardAdminSession(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(SESSION_KEY) === '1';
}
